import NavigationSider from '@hellodarwin/app-core/lib/layouts/navigation';
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import { NavigationLink } from '@hellodarwin/core/lib/features/entities';
import useMediaQuery from '@hellodarwin/core/lib/features/helpers/use-media-query';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import User from '@hellodarwin/icons/dist/icons/User';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../app/index';
import { useClientAnalytics } from '../features/analytics/use-client-analytics';
import {
  fetchApplicationNotifications,
  selectApplicationNotificationTotal,
} from '../features/api/slices/client-application-notifications-slice';
import { selectCompanyAdmin } from '../features/api/slices/global-slice';
import { selectProfile } from '../features/api/slices/profile-slice';
import { useClientApi, useNewClientApi } from '../features/api/use-client-api';
import {
  loadDocumentRequests,
  selectDashboardDocumentRequests,
} from '../components/dashboard/document-requests';
import { buildNavigationLinks } from './navigation-links';

const Navigation = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: hdT } = useTranslations();
  const { selectedLocale } = useLocale();
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.tablet });
  const profile = useAppSelector(selectProfile);
  const { accessLevel } = usePremiumAccess();
  const admin = useAppSelector(selectCompanyAdmin);
  const notificationTotal = useAppSelector(selectApplicationNotificationTotal);
  const documentRequests = useAppSelector(selectDashboardDocumentRequests);
  const api = useClientApi();
  const newApi = useNewClientApi();
  const analytics = useClientAnalytics();

  // Product rule: the applications badge is Custom-only. Still load-bearing at render
  // time — the applications page fetches for every tier, so the store can hold counts
  // even when the badge is hidden.
  const showsNotificationBadge = accessLevel === 'custom';

  useEffect(() => {
    if (!showsNotificationBadge) return;
    dispatch(fetchApplicationNotifications({ api: newApi }));
  }, [dispatch, newApi, showsNotificationBadge]);

  useEffect(() => {
    if (!showsNotificationBadge) return;
    void loadDocumentRequests({
      dispatch,
      api,
      newApi,
      locale: selectedLocale,
    });
  }, [api, dispatch, newApi, selectedLocale, showsNotificationBadge]);

  const documentRequestTotal = documentRequests.length;

  const links = useMemo(() => {
    return buildNavigationLinks({
      accessLevel,
      applicationNotifications: showsNotificationBadge ? notificationTotal : 0,
      documentRequests: showsNotificationBadge ? documentRequestTotal : 0,
      isTablet,
      labels: {
        dashboard: t('navigation.dashboard'),
        planning: hdT('navigation|planning'),
        documents: hdT('documents|navigationTitle'),
        fundingExplorer: hdT('navigation|fundingExplorer2'),
        assistant: hdT('navigation|assistant'),
        applications: hdT('navigation|applications'),
        fundingIntelligence: hdT('navigation|fundingIntelligence'),
        alerts: hdT('navigation|alerts'),
        aiIntegration: hdT('navigation|aiIntegration'),
        tools: t('navigation.tools'),
        support: hdT('navigation|support'),
        resources: t('resource|resources'),
        faq: hdT('navigation|faq'),
        contactUs: hdT('navigation|contactUs'),
      },
    });
  }, [
    isTablet,
    t,
    hdT,
    notificationTotal,
    showsNotificationBadge,
    accessLevel,
    documentRequestTotal,
  ]);

  const userLinks: NavigationLink[] = useMemo(() => {
    return [
      {
        label: t('navigation.preferences'),
        pathname: '/profile',
        Icon: User,
      },
    ];
  }, [t]);

  return (
    <NavigationSider
      links={links}
      app="client"
      userLinks={userLinks}
      profile={profile}
      admin={admin}
      defaultOpen={false}
      showPremiumStatus={accessLevel !== 'custom'}
      openMeetingModalClicked={analytics.openMeetingModalClicked}
    />
  );
};

export default Navigation;
